import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { selectLayoutUser, useCurrentProjectData } from '@/entities/layout';
import {
  createNavigationItemsWithStateSelector,
  selectIsCollapsed,
  selectIsMobileOpen,
  toggleItem,
  toggleSidebar,
  setMobileSidebarOpen,
  withBadges
} from '@/entities/navigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useProjectSiteParams } from '@/shared/lib';
import type { AppDispatch, RootState } from '@/app/types/store';

export const useSidebar = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();
  const { projectId, siteId } = useProjectSiteParams();

  const projectData = useCurrentProjectData();
  const currentProject = projectData?.project;
  const currentSite = projectData?.site;
  const instanceInfo = projectData?.instance;
  const badges = projectData?.menu?.badges;

  const user = useSelector(selectLayoutUser);
  const isCollapsed = useSelector(selectIsCollapsed);
  const isMobileOpen = useSelector(selectIsMobileOpen);

  const navigationItemsSelector = useMemo(
    () => createNavigationItemsWithStateSelector(projectId, siteId),
    [projectId, siteId]
  );

  const navigationItems = useSelector((state: RootState) => navigationItemsSelector(state, location.pathname));

  useEffect(() => {
    if (isMobile && isMobileOpen) {
      dispatch(setMobileSidebarOpen(false));
    }
  }, [location.pathname, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemsWithBadges = useMemo(() => withBadges(navigationItems, badges ?? undefined), [navigationItems, badges]);

  const handleToggleItem = (code: string) => {
    dispatch(toggleItem(code));
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleOpenMobile = () => {
    dispatch(setMobileSidebarOpen(true));
  };

  const handleCloseMobile = () => {
    dispatch(setMobileSidebarOpen(false));
  };

  return {
    // State
    user,
    currentProject,
    currentSite,
    instanceInfo,
    isCollapsed: isMobile ? false : isCollapsed,
    isMobileOpen,
    isMobile,
    items: itemsWithBadges,

    // Actions
    onToggleItem: handleToggleItem,
    onToggleSidebar: handleToggleSidebar,
    onOpenMobile: handleOpenMobile,
    onCloseMobile: handleCloseMobile
  };
};
